import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css, keyframes } from "styled-components";

import {
  Button,
  DesignSystemSurface,
  EmptyState,
  Input,
  Skeleton,
  Tag,
} from "../../../design-system";
import { fetchBrainAtlas } from "../../../database/brain/brainAtlasApi";
import {
  BrainJob,
  BrainJobStatus,
  BrainTask,
  FREEFORM_TASK_ID,
  dispatchBrainJob,
  fetchBrainCatalog,
  fetchBrainJob,
  fetchBrainJobs,
} from "../../../database/brain/brainJobsApi";

// The Second Brain mission panel (/brain). Tribed-team only: the nav entry is
// hidden for coaches, and every function behind these tabs re-checks the
// caller, so this screen is a viewer, not the gate.
//
// Layout is a Cursor-style chat workspace:
//   left  - thread list, one row per mission, newest first
//   right - the selected mission as a conversation (prompt bubble, then the
//           runner's log streaming below it), with the composer pinned at the
//           bottom like an IDE chat panel
//
// The composer offers free text only when brainCatalog published `freeform`,
// which the server does for role 101 alone. A VA sees the same screen with the
// catalog tasks in the picker and form fields instead of the textarea. That is
// presentation following the gate, not enforcing it: brainDispatch denies the
// task regardless of what is rendered.

const HEADER_OFFSET = 104;

const LABEL: Record<string, string> = {
  coach_name: "Coach name",
  website: "Website",
  linkedin: "LinkedIn",
  language: "Language",
  business_name: "Business name",
  whatsapp: "WhatsApp number",
  country: "Country",
};

// Status drives the red/green the panel exists to provide: a routine that has
// silently stopped is the failure mode the Claude-app scheduled tasks cannot
// show, so `failed` and `rejected` must read as loud, not neutral.
const STATUS_VARIANT: Record<
  BrainJobStatus,
  "neutral" | "accent" | "success" | "warning" | "danger" | "info"
> = {
  queued: "neutral",
  running: "info",
  succeeded: "success",
  failed: "danger",
  rejected: "warning",
};

const STATUS_DOT: Record<BrainJobStatus, string> = {
  queued: "var(--color-text-secondary)",
  running: "var(--color-accent)",
  succeeded: "var(--color-success-fg, #2e9e5b)",
  failed: "var(--color-danger-fg, #c0392b)",
  rejected: "var(--color-warning-fg, #b8860b)",
};

const isTerminal = (status: BrainJobStatus): boolean =>
  status === "succeeded" || status === "failed" || status === "rejected";

const when = (ms: number | null): string =>
  ms ? new Date(ms).toLocaleString() : "—";

// Compact relative time for the thread list; the absolute time stays in the
// thread header.
const ago = (ms: number | null): string => {
  if (!ms) return "";
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

// Routine runs are the bulk of the list (the box timers enqueue them), so a
// routine thread is named by its slug, which IS the routine's name.
const ROUTINE_TASK_ID = "routine";

export const jobTitle = (job: BrainJob, tasks: BrainTask[] | null): string => {
  if (job.taskId === FREEFORM_TASK_ID) {
    const first = (job.params.prompt || "").trim().split("\n")[0];
    return first || "(no prompt)";
  }
  if (job.taskId === ROUTINE_TASK_ID && job.params.routine) {
    return job.params.routine;
  }
  return tasks?.find((t) => t.id === job.taskId)?.label || job.taskId;
};

// A follow-up runs as a fresh freeform mission: the box runner has no session
// resume, so continuity is the previous mission quoted into the new prompt.
// The user's instruction stays on line one, so it also names the new thread.
export const buildFollowUpPrompt = (
  text: string,
  prev: BrainJob,
  tasks: BrainTask[] | null
): string => {
  const lines = [
    text,
    "",
    `--- Follow-up on brain mission ${prev.id} (${prev.status}) ---`,
    `Mission: ${jobTitle(prev, tasks)}`,
  ];
  if (prev.taskId === FREEFORM_TASK_ID && prev.params.prompt) {
    lines.push(`Original prompt: ${prev.params.prompt}`);
  } else {
    const params = Object.entries(prev.params)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");
    if (params) lines.push(`Params: ${params}`);
  }
  if (prev.deliverable) lines.push(`Deliverable: ${prev.deliverable}`);
  const tail = (prev.logTail || "").slice(-2000);
  if (tail) {
    lines.push("Log tail of that run:", "```", tail, "```");
  }
  lines.push(
    "Continue from where that mission left off and do what the first line asks."
  );
  return lines.join("\n");
};

// --- Frame -----------------------------------------------------------------

const Page = styled.div`
  height: calc(100vh - ${HEADER_OFFSET}px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: 0 var(--space-5);
  border-bottom: 1px solid var(--color-border-subtle);
  flex: none;
`;

const Title = styled.h1`
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
  padding: var(--space-3) 0;
`;

const TabRow = styled.div`
  display: flex;
  gap: var(--space-1);
`;

const TabButton = styled.button<{ $active: boolean }>`
  appearance: none;
  background: none;
  border: none;
  border-bottom: 2px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "transparent")};
  color: ${(p) =>
    p.$active ? "var(--color-text-primary)" : "var(--color-text-secondary)"};
  font-size: var(--font-size-sm);
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  padding: var(--space-3) var(--space-2);
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

// --- Sidebar (thread list) -------------------------------------------------

const Sidebar = styled.aside`
  width: 280px;
  flex: none;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--color-border-subtle);
  background: var(--color-surface-sunken, rgba(0, 0, 0, 0.03));
`;

const SidebarHead = styled.div`
  padding: var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const ThreadList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-2);
  display: grid;
  gap: 2px;
  align-content: start;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const Dot = styled.span<{ $status: BrainJobStatus }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
  background: ${(p) => STATUS_DOT[p.$status]};
  ${(p) =>
    p.$status === "running" &&
    css`
      animation: ${pulse} 1.4s ease-in-out infinite;
    `}
`;

const ThreadRow = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: ${(p) =>
    p.$active ? "var(--color-surface-raised, rgba(0, 0, 0, 0.06))" : "none"};
  color: var(--color-text-primary);
  text-align: left;
  cursor: pointer;
  transition: background 100ms ease;

  &:hover {
    background: var(--color-surface-raised, rgba(0, 0, 0, 0.06));
  }
`;

const ThreadTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThreadTime = styled.span`
  flex: none;
  font-size: 11px;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
`;

// --- Thread (conversation) -------------------------------------------------

const Main = styled.section`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-5) var(--space-6);
`;

const ThreadInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  display: grid;
  gap: var(--space-4);
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

// The user's turn, right-aligned like a chat bubble. A freeform prompt is the
// job's identity, so it is shown in full rather than truncated, as plain text
// preserving its own whitespace, so a prompt cannot be laid out to impersonate
// the panel's own chrome.
const PromptBubble = styled.div`
  justify-self: end;
  max-width: 85%;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-surface-raised, rgba(0, 0, 0, 0.06));
  border: 1px solid var(--color-border-subtle);
`;

const PromptText = styled.p`
  margin: 0;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

// The runner's turn: the log tail as a terminal-like block, full width.
const OutputBlock = styled.pre`
  margin: 0;
  padding: var(--space-4);
  background: var(--color-surface-sunken, rgba(0, 0, 0, 0.04));
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  font-family: var(--font-family-mono, ui-monospace, SFMono-Regular, monospace);
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--color-text-secondary);
`;

const Deliverable = styled.a`
  font-size: var(--font-size-sm);
  word-break: break-all;
`;

const Message = styled.p<{ $error?: boolean }>`
  margin: 0;
  font-size: var(--font-size-sm);
  color: ${(p) =>
    p.$error
      ? "var(--color-danger-fg, #c0392b)"
      : "var(--color-text-secondary)"};
`;

// --- Composer --------------------------------------------------------------

const ComposerDock = styled.div`
  flex: none;
  padding: var(--space-3) var(--space-6) var(--space-5);
`;

const ComposerBox = styled.form`
  max-width: 820px;
  margin: 0 auto;
  border: 1px solid var(--color-border-strong, var(--color-border-subtle));
  border-radius: var(--radius-md);
  background: var(--color-surface, transparent);
  display: grid;
  transition: border-color 120ms ease, box-shadow 120ms ease;

  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 1px var(--color-accent);
  }
`;

const PromptArea = styled.textarea`
  appearance: none;
  border: none;
  outline: none;
  background: none;
  resize: none;
  width: 100%;
  padding: var(--space-3) var(--space-4) var(--space-1);
  font-family: inherit;
  font-size: var(--font-size-base);
  line-height: 1.5;
  color: var(--color-text-primary);
  max-height: 220px;

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const FieldsStack = styled.div`
  display: grid;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-4) var(--space-1);
`;

const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3) var(--space-3);
`;

// The task picker sits in the composer footer like Cursor's model chip.
const TaskPicker = styled.select`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken, rgba(0, 0, 0, 0.04));
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  padding: 2px var(--space-3);
  cursor: pointer;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const Hint = styled.span`
  font-size: 11px;
  color: var(--color-text-secondary);
  margin-left: auto;
`;

// Marks the composer as continuing the open thread, like Cursor's context
// chip. Ellipsized so a long routine slug cannot push the send button out.
const ContinueChip = styled.span`
  font-size: 11px;
  color: var(--color-accent);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  padding: 2px var(--space-2);
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ComposerMessages = styled.div`
  max-width: 820px;
  margin: var(--space-2) auto 0;
`;

// --- Atlas -----------------------------------------------------------------

const Frame = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: #0b0b0e;
`;

// The atlas is a self-contained document (its own <style> and <script>) built
// by the SecondBrain vault. It is rendered into a sandboxed iframe:
// allow-scripts without allow-same-origin gives it an opaque origin, so it can
// run its canvas graph but cannot reach NobleAdmin's storage, cookies, or
// Firebase session.
const AtlasTab: React.FC = () => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchBrainAtlas()
      .then((markup) => {
        if (!cancelled) setHtml(markup);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <EmptyState title="Atlas unavailable" description={error} />;
  if (html === null) {
    return (
      <EmptyState
        title="Loading the atlas…"
        description="Fetching the latest graph build."
      />
    );
  }
  return (
    <Frame title="Second Brain atlas" sandbox="allow-scripts" srcDoc={html} />
  );
};

// --- Thread view -----------------------------------------------------------

// One mission rendered as a conversation. The list endpoint omits logTail (it
// would be 4 KB per row), so the full job is fetched here and re-fetched every
// few seconds while the runner is still on it, which is what makes the output
// read as streaming.
const ThreadView: React.FC<{ job: BrainJob; tasks: BrainTask[] | null }> = ({
  job,
  tasks,
}) => {
  const [full, setFull] = useState<BrainJob>(job);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);

  const live = !isTerminal(full.status);

  const load = useCallback(async () => {
    try {
      const { job: next } = await fetchBrainJob(job.id);
      setFull(next);
    } catch {
      // Keep the last good snapshot; the next poll retries.
    }
  }, [job.id]);

  useEffect(() => {
    setFull(job);
    pinnedRef.current = true;
    load();
  }, [job, load]);

  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [live, load]);

  // Follow the log like a terminal, but only while the reader is at the
  // bottom, so scrolling up to read is not fought by the poller.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [full.logTail, full.status]);

  const isFreeform = full.taskId === FREEFORM_TASK_ID;
  // Skip the param the heading already is: a freeform job's prompt, and a
  // routine's slug, which jobTitle renders as the title.
  const paramLine = Object.entries(full.params)
    .filter(([k]) => !(isFreeform && k === "prompt"))
    .filter(([k]) => !(full.taskId === ROUTINE_TASK_ID && k === "routine"))
    .map(([k, v]) => `${LABEL[k] || k}: ${v}`)
    .join(" · ");

  return (
    <Scroll ref={scrollRef} onScroll={onScroll}>
      <ThreadInner>
        <MetaLine>
          <Tag variant={STATUS_VARIANT[full.status] || "neutral"} withDot>
            {full.status}
          </Tag>
          <span>
            {full.requestedBy?.email || "unknown"} · {when(full.requestedAt)}
            {full.finishedAt ? ` · finished ${when(full.finishedAt)}` : ""}
          </span>
        </MetaLine>

        <PromptBubble>
          {isFreeform ? (
            <PromptText>{full.params.prompt || "(no prompt)"}</PromptText>
          ) : (
            <PromptText>
              {jobTitle(full, tasks)}
              {paramLine ? `\n${paramLine}` : ""}
            </PromptText>
          )}
        </PromptBubble>

        {full.logTail ? (
          <OutputBlock>{full.logTail}</OutputBlock>
        ) : (
          <Message>
            {live ? "Waiting for the first output…" : "No log captured."}
          </Message>
        )}

        {full.error && <Message $error>{full.error}</Message>}

        {full.deliverable && (
          <Deliverable
            href={full.deliverable}
            target="_blank"
            rel="noreferrer noopener"
          >
            {full.deliverable}
          </Deliverable>
        )}
      </ThreadInner>
    </Scroll>
  );
};

// --- Composer --------------------------------------------------------------

const BrainComposer: React.FC<{
  tasks: BrainTask[];
  continueJob: BrainJob | null;
  onDispatched: (jobId: string) => void;
}> = ({ tasks, continueJob, onDispatched }) => {
  const [taskId, setTaskId] = useState<string>(() => {
    // Free text first when the server offers it; it is the common case for
    // the one operator who has it.
    const preferred = tasks.find((t) => t.id === FREEFORM_TASK_ID) || tasks[0];
    return preferred?.id || "";
  });
  const [prompt, setPrompt] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  const task = useMemo(
    () => tasks.find((t) => t.id === taskId) || null,
    [tasks, taskId]
  );
  const isFreeform = task?.id === FREEFORM_TASK_ID;

  // Continuing needs freeform (the context rides in the prompt), so the mode
  // only lights up for callers the server gave a textarea to.
  const continuing = Boolean(continueJob && isFreeform);

  const grow = () => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  };

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!task || busy) return;

    // Busy goes up before the follow-up's context fetch, so a second Enter
    // during that await cannot race a duplicate job in.
    setBusy(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (isFreeform) {
        const text = prompt.trim();
        if (!text) return;
        if (continuing && continueJob) {
          // The list row lacks logTail, so the full job is fetched at send
          // time to quote the freshest log into the follow-up. If the fetch
          // fails the follow-up still goes out with the list snapshot.
          let prev = continueJob;
          try {
            const { job: fullPrev } = await fetchBrainJob(continueJob.id);
            prev = fullPrev;
          } catch {
            // Fall back to the list snapshot.
          }
          params.prompt = buildFollowUpPrompt(text, prev, tasks);
        } else {
          params.prompt = text;
        }
      } else {
        // Send only non-empty fields: the server rejects unknown keys, and an
        // empty optional would fail its pattern rather than be treated as
        // absent.
        task.fields.forEach(({ name }) => {
          const value = (values[name] || "").trim();
          if (value) params[name] = value;
        });
      }

      const { jobId } = await dispatchBrainJob(task.id, params);
      setPrompt("");
      setValues({});
      if (areaRef.current) areaRef.current.style.height = "auto";
      onDispatched(jobId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Enter sends and Shift+Enter breaks the line, the chat-panel default.
  // Cmd/Ctrl+Enter also still sends, for muscle memory from the old panel.
  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const canSend = isFreeform ? Boolean(prompt.trim()) : true;

  return (
    <ComposerDock>
      <ComposerBox onSubmit={submit}>
        {isFreeform ? (
          <PromptArea
            ref={areaRef}
            rows={1}
            placeholder={
              continuing && continueJob
                ? `Continue "${jobTitle(continueJob, tasks)}"…`
                : "Ask the brain to do something…"
            }
            value={prompt}
            disabled={busy}
            onKeyDown={onKeyDown}
            onChange={(e) => {
              setPrompt(e.target.value);
              grow();
            }}
          />
        ) : (
          <FieldsStack>
            {task?.fields.map(({ name, required }) => (
              <Input
                key={name}
                label={`${LABEL[name] || name}${required ? "" : " (optional)"}`}
                value={values[name] || ""}
                fullWidth
                disabled={busy}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [name]: e.target.value }))
                }
              />
            ))}
          </FieldsStack>
        )}

        <ComposerFooter>
          {tasks.length > 1 && (
            <TaskPicker
              aria-label="Task"
              value={taskId}
              disabled={busy}
              onChange={(e) => {
                setTaskId(e.target.value);
                setValues({});
                setError(null);
              }}
            >
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </TaskPicker>
          )}
          {continuing && continueJob && (
            <ContinueChip title="This message runs a follow-up mission carrying the open thread's prompt and log as context.">
              ↩ {jobTitle(continueJob, tasks)}
            </ContinueChip>
          )}
          <Hint>{isFreeform ? "↵ send · ⇧↵ newline" : ""}</Hint>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={busy || !canSend}
          >
            {busy ? "Queueing…" : "Send"}
          </Button>
        </ComposerFooter>
      </ComposerBox>

      {error && (
        <ComposerMessages>
          <Message $error>{error}</Message>
        </ComposerMessages>
      )}
    </ComposerDock>
  );
};

// --- Missions --------------------------------------------------------------

const MissionsTab: React.FC = () => {
  const [tasks, setTasks] = useState<BrainTask[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<BrainJob[] | null>(null);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchBrainCatalog()
      .then(({ tasks: list }) => {
        if (!cancelled) setTasks(list);
      })
      .catch((e: Error) => {
        if (!cancelled) setCatalogError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const { jobs: list } = await fetchBrainJobs(50);
      setJobs(list);
      setJobsError(null);
    } catch (e: any) {
      setJobsError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll the list only while something is in flight, so an idle panel is not
  // chatty. The open thread has its own faster poll for the log.
  useEffect(() => {
    const active = jobs?.some((job) => !isTerminal(job.status));
    if (!active) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [jobs, load]);

  const selected = useMemo(
    () => jobs?.find((j) => j.id === selectedId) || null,
    [jobs, selectedId]
  );

  const onDispatched = (jobId: string) => {
    setSelectedId(jobId);
    load();
  };

  return (
    <Body>
      <Sidebar>
        <SidebarHead>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => setSelectedId(null)}
          >
            + New mission
          </Button>
        </SidebarHead>

        <ThreadList>
          {!jobs && !jobsError && (
            <>
              <Skeleton height={30} />
              <Skeleton height={30} />
              <Skeleton height={30} />
            </>
          )}
          {jobsError && !jobs && <Message $error>{jobsError}</Message>}
          {jobs?.map((job) => (
            <ThreadRow
              key={job.id}
              type="button"
              $active={job.id === selectedId}
              onClick={() => setSelectedId(job.id)}
            >
              <Dot $status={job.status} />
              <ThreadTitle>{jobTitle(job, tasks)}</ThreadTitle>
              <ThreadTime>{ago(job.requestedAt)}</ThreadTime>
            </ThreadRow>
          ))}
          {jobs && !jobs.length && <Message>No missions yet.</Message>}
        </ThreadList>
      </Sidebar>

      <Main>
        {selected ? (
          <ThreadView job={selected} tasks={tasks} />
        ) : (
          <Scroll>
            <ThreadInner>
              <EmptyState
                title="Ask the Second Brain"
                description="It runs as a Claude session on tribed-workers with the full MCP toolkit, in a scratch workspace. Pick a past mission on the left to reread it."
              />
            </ThreadInner>
          </Scroll>
        )}

        {catalogError && (
          <ComposerDock>
            <ComposerMessages>
              <Message $error>{catalogError}</Message>
            </ComposerMessages>
          </ComposerDock>
        )}
        {!tasks && !catalogError && (
          <ComposerDock>
            <Skeleton height={72} />
          </ComposerDock>
        )}
        {tasks && tasks.length > 0 && (
          <BrainComposer
            tasks={tasks}
            continueJob={selected}
            onDispatched={onDispatched}
          />
        )}
        {tasks && !tasks.length && (
          <ComposerDock>
            <ComposerMessages>
              <Message>
                The server published an empty catalog for this account.
              </Message>
            </ComposerMessages>
          </ComposerDock>
        )}
      </Main>
    </Body>
  );
};

// --- Screen ----------------------------------------------------------------

type TabId = "missions" | "atlas";

const TABS: { id: TabId; label: string }[] = [
  { id: "missions", label: "Missions" },
  { id: "atlas", label: "Atlas" },
];

const BrainScreen: React.FC = () => {
  const [tab, setTab] = useState<TabId>("missions");

  return (
    <DesignSystemSurface>
      <Page>
        <TopBar>
          <Title>Brain</Title>
          <TabRow role="tablist">
            {TABS.map(({ id, label }) => (
              <TabButton
                key={id}
                role="tab"
                type="button"
                aria-selected={tab === id}
                $active={tab === id}
                onClick={() => setTab(id)}
              >
                {label}
              </TabButton>
            ))}
          </TabRow>
        </TopBar>

        {tab === "missions" && <MissionsTab />}
        {tab === "atlas" && <AtlasTab />}
      </Page>
    </DesignSystemSurface>
  );
};

export default BrainScreen;
